import React from 'react';

/**
 * Schema Markup Component - Add structured data for Google
 * 
 * Types:
 * - organization: Company/brand info
 * - property: Individual property listing
 * - breadcrumb: Navigation breadcrumbs
 * - local-business: Local business info
 */

export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Homesbyli",
    "legalName": "Homesbyli",
    "alternateName": "Homes by Li",
    "url": "https://homesbyli.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://homesbyli.com/logo.png",
      "width": "200",
      "height": "60"
    },
    "description": "Your gateway to unforgettable Montreal experiences. Find the perfect short-term rental in Montreal. Quality apartments, amazing experiences, and authentic Montreal living.",
    "slogan": "Your Gateway to Unforgettable Montreal",
    "foundingDate": "2026",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Montreal",
      "addressRegion": "QC",
      "addressCountry": "CA"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "email": "booking@homesbyli.com",
        "contactType": "Reservations",
        "areaServed": "CA",
        "availableLanguage": ["English", "French"]
      },
      {
        "@type": "ContactPoint",
        "email": "support@homesbyli.com",
        "contactType": "Customer Support",
        "areaServed": "CA",
        "availableLanguage": ["English", "French"]
      }
    ],
    "sameAs": [
      "https://www.facebook.com/homesbyli",
      "https://www.instagram.com/homesbyli",
      "https://twitter.com/homesbyli"
    ],
    "areaServed": {
      "@type": "City",
      "name": "Montreal",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Quebec",
        "addressCountry": "Canada"
      }
    },
    "knowsAbout": [
      "Short-term rentals",
      "Montreal accommodation",
      "Vacation rentals",
      "Student housing",
      "Medical professional housing",
      "Montreal tourism"
    ],
    "brand": {
      "@type": "Brand",
      "name": "Homesbyli"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const PropertySchema = ({ property }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Accommodation",
    "name": property.title,
    "description": property.description,
    "image": property.images,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": property.address,
      "addressLocality": property.city || "Montreal",
      "addressRegion": "QC",
      "addressCountry": "CA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": property.latitude,
      "longitude": property.longitude
    },
    "numberOfRooms": property.bedrooms,
    "petsAllowed": property.petsAllowed || false,
    "priceRange": `$${property.price}`,
    "amenityFeature": property.amenities?.map(amenity => ({
      "@type": "LocationFeatureSpecification",
      "name": amenity
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "Homesbyli",
    "image": "https://homesbyli.com/logo.png",
    "url": "https://homesbyli.com",
    "telephone": "+1-514-XXX-XXXX",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Montreal",
      "addressLocality": "Montreal",
      "addressRegion": "QC",
      "postalCode": "H2X",
      "addressCountry": "CA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 45.5017,
      "longitude": -73.5673
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      "opens": "00:00",
      "closes": "23:59"
    },
    "servesCuisine": "Accommodation",
    "areaServed": {
      "@type": "City",
      "name": "Montreal"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};
